import React, { useState, useEffect } from 'react'

// Appt Components
import Box from '../../components/simple/box/Box'
import Button from '../../components/simple/button/Button'
import DataTable from '../../components/simple/dataTable/DataTable.js'
import FontAwesome from '../../components/compound/fontAwesome/FontAwesome'
import Form from '../../components/simple/form/Form.js'
import FormField from '../../components/simple/formField/FormField.js'
import Divider from '../../components/simple/divider/Divider'
import H2 from '../../components/simple/heading/H2.js'
import OptionButtons from '../../components/compound/optionButtons/OptionButtons'
import RadioButtonGroup from '../../components/simple/input/RadioButtonGroup'
import Select from '../../components/simple/input/Select'
import Text from '../../components/simple/text/Text'
import TextArea from '../../components/simple/input/TextArea'
import TextInput from '../../components/simple/input/TextInput'
import LocalStorageService from '../../services/LocalStorageService'


import { capitalize } from '../../utils/text'
import constants from '../../utils/constants'

function MedicalIntervention (props) {
  const [questions, setQuestions] = useState(props.questions)
  const [interventionAddButton, setInterventionAddButton] = useState(false)
  const [localIncidentValues, setLocalIncidentValues] = useState(props.incidentValues)
  const [editingDisabled] = useState(props.formId)

  const terms = LocalStorageService.get('terms')

  const previousPage = () => {
    props.previousPage(localIncidentValues)
  }

  const nextPage = () => {
    props.nextPage(localIncidentValues)
  }

  // Submit form values
  const submitForm = async () => {
    props.submitForm(localIncidentValues, 'Draft')
  }

  // Interventions have been altered
  // so rebuild the display list
  const [mappedInterventions, setMappedInterventions] = useState([])
  const [interventionColumns, setInterventionDataColumns] = useState()

  const addIntervention = () => {
    const intervention = {
      interventionKey: props.interventions.length,
      injurySufferedBy: localIncidentValues.injurySufferedBy,
      injury: localIncidentValues.injury,
      treatment: localIncidentValues.treatment,
      injurySufferedName: localIncidentValues.injurySufferedName,
    }
    props.setIntervention(interventions => [...interventions, intervention])

    setLocalIncidentValues(localIncidentValues => ({
      ...localIncidentValues,
      injurySufferedBy: '',
      injury: '',
      treatment: '',
      injurySufferedName: '',
    }))
    setInterventionAddButton(false)
  }

  const removeIntervention = (data) => {
    const newInterventions = props.interventions.filter((item) => item.interventionKey !== data.interventionKey)

    props.setIntervention(newInterventions)
  }

  useEffect(() => {
    setInterventionDataColumns([
      {
        property: 'injurySufferedBy',
        header: <Text pad='none' margin='none'>Injury Suffered by</Text>
      },
      {
        property: 'injury',
        header: <Text>Injury</Text>
      },
      {
        property: 'treatment',
        header: <Text>Treatment</Text>
      },
      {
        property: 'injurySufferedName',
        header: <Text>Name</Text>
      },
      {
        property: 'actions',
        render: datum => (
          <Box direction='row'>
            <Button label={<Text><FontAwesome icon={['fal', 'minus-circle']} /></Text>} margin={{ horizontal: 'xsmall' }} style={{ background: '#333' }} onClick={() => removeIntervention(datum)} target='_self' />
          </Box>
        )
      }
    ])

    const mappedInterventions = props.interventions.map((intervention, index) => ({
      interventionKey: index,
      injurySufferedBy: intervention.injurySufferedBy,
      injury: intervention.injury,
      treatment: intervention.treatment,
      injurySufferedName: intervention.injurySufferedName,
    }))

    setMappedInterventions(mappedInterventions)
  }, [props.interventions])


  var sufferedByOptions = []
  for (var thisField of constants.injurySufferedBy) {
    if (thisField?.includes('[Student]')) {
      thisField = thisField.replace('[Student]', capitalize(terms?.serviceUser || 'Service User'))
    }
    sufferedByOptions.push(thisField)
  }

  return (
    <Box gridArea='main' background='white' direction='column' gap='small' round='small'>
      <Box
        gap='small'
        margin={{ horizontal: 'small' }}
        pad='small'
        round='small'
      >
        <Box fill direction='row' gap='small'>
          <H2 margin={{ vertical: 'none' }}>Other Intervention</H2>
          <Button
            label={<Text><FontAwesome color='brand' icon={['fas', 'question-circle']} /></Text>}
            plain
            tip={{
              content: (
                <Box background={{ color: 'white' }} border={{ color: 'brand', size: 'small' }} direction='column' pad='small'>
                  <Text>
                    The difference between a restrictive physical intervention and a physical intervention is <strong>the use of force</strong>. For example, a physical intervention may be guiding an individual who is going with you willingly, but if the individual is resisting and force is used it becomes a restrictive physical intervention.
                  </Text>
                </Box>)
            }}
          />
        </Box>
        <Divider color='primary' margin={{ top: 'none', bottom: 'small' }} />
        <Form
          onChange={nextValue => {
            // Enable Intervention Add button if all fields have data
            setInterventionAddButton(nextValue.injurySufferedBy !== '' && nextValue.injury !== '' && nextValue.treatment !== '' && nextValue.injurySufferedName !== '')

            setLocalIncidentValues(nextValue)
          }}
          onSubmit={({ value: nextValue }) => {
            submitForm(nextValue)
          }}
          value={localIncidentValues}
        >
          <Box>

            <Box direction='row-responsive' gap='small'>
              {/* physical intervention used ? */}
              <FormField
                label={questions[9].questionText}
                name={questions[9].key}
                required
              >
                {!editingDisabled && <OptionButtons
                  id={questions[9].key}
                  name={questions[9].key}
                  value={localIncidentValues.physicalInterventionUsed || 'No'}
                />}
                {editingDisabled && <Text>{props.incidentValues?.[questions[9].key] || ''}</Text>}

              </FormField>
            </Box>

            <Box direction='row-responsive' gap='small'>
              {/* restricted physical intervention used ? */}
              <FormField
                label={questions[7].questionText}
                name={questions[7].key}
                required
              >
                {!editingDisabled && <OptionButtons
                  id={questions[7].key}
                  name={questions[7].key}
                  value={localIncidentValues.restrictedPhysicalInterventionUsed || 'No'}
                />}
                {editingDisabled && <Text>{props.incidentValues?.[questions[7].key] || ''}</Text>}

              </FormField>
            </Box>

            {localIncidentValues.restrictedPhysicalInterventionUsed === 'Yes' &&
              <Box direction='row-responsive' gap='small'>
                {/* Breathing monitored */}
                <FormField
                  label={questions[0].questionText}
                  name={questions[0].key}
                  required
                >
                  {!editingDisabled && <RadioButtonGroup
                    direction='row'
                    name={questions[0].key}
                    // onChange={event =>
                    //   setActionButton(event.target.value)}
                    options={['Yes', 'No']}
                  >
                    {(option, { checked, hover }) => {
                      let background
                      if (checked) background = 'brand'
                      else if (hover) background = 'light-4'
                      else background = 'light-2'
                      return (
                        <Box background={background} pad='small'>
                          {option}
                        </Box>
                      )
                    }}
                  </RadioButtonGroup>}
                  {editingDisabled && <Text>{props.incidentValues?.[questions[0].key] ? 'Yes' : 'No'}</Text>}

                </FormField>

                {/* Duration */}
                <FormField
                  label={questions[1].questionText}
                  name={questions[1].key}
                  required
                >
                  {!editingDisabled && <TextInput
                    name={questions[1].key}
                    type='number'
                  />}
                  {editingDisabled && <Text>{props.incidentValues?.[questions[1].key] || ''}</Text>}

                </FormField>

                {/* Number of staff */}
                <FormField
                  label={questions[2].questionText}
                  name={questions[2].key}
                  required
                >
                  {!editingDisabled && <TextInput
                    name={questions[2].key}
                    type='number'
                  />}
                  {editingDisabled && <Text>{props.incidentValues?.[questions[2].key] || ''}</Text>}

                </FormField>
              </Box>}

            <Box direction='row-responsive' gap='small'>
              {/* medical intervention required ? */}
              <FormField
                label={questions[8].questionText}
                name={questions[8].key}
                required
              >
                {!editingDisabled && <OptionButtons
                  id={questions[8].key}
                  name={questions[8].key}
                  value={localIncidentValues.medicalInterventionRequired || 'No'}
                />}
                {editingDisabled && <Text>{props.incidentValues?.[questions[8].key] ? 'Yes' : 'No'}</Text>}

              </FormField>
            </Box>

            {localIncidentValues.medicalInterventionRequired === 'Yes' &&
              <>
                <Box direction='row-responsive' gap='medium'>
                  <DataTable
                    columns={interventionColumns}
                    data={mappedInterventions}
                    fill='horizontal'
                    primaryKey={false}
                    size='large'
                  />
                </Box>
                <Box direction='row-responsive' gap='medium'>
                  {/* Injury Suffered By */}
                  <FormField
                    label={questions[3].questionText}
                    name={questions[3].key}
                  >
                    {!editingDisabled && <Select
                      name={questions[3].key}
                      options={sufferedByOptions}
                    />}
                    {editingDisabled && <Text>{props.incidentValues?.[questions[3].key] || ''}</Text>}

                  </FormField>

                  <FormField
                    label={questions[4].questionText}
                    name={questions[4].key}
                  >
                    {!editingDisabled && <TextInput
                      name={questions[4].key}
                      type='text'
                    />}
                    {editingDisabled && <Text>{props.incidentValues?.[questions[4].key] || ''}</Text>}
                  </FormField>

                  <FormField
                    label={questions[5].questionText}
                    name={questions[5].key}
                  >
                    {!editingDisabled && <TextInput
                      name={questions[5].key}
                      type='text'
                    />}
                    {editingDisabled && <Text>{props.incidentValues?.[questions[5].key] || ''}</Text>}
                  </FormField>


                  <FormField
                    label={questions[10].questionText}
                    name={questions[10].key}
                  >
                    {!editingDisabled && <TextInput
                      name={questions[10].key}
                      type='text'
                    />}
                    {editingDisabled && <Text>{props.incidentValues?.[questions[10].key] || ''}</Text>}
                  </FormField>


                  {!editingDisabled && <Button disabled={!interventionAddButton} label={<Text><FontAwesome icon={['fal', 'plus-circle']} /></Text>} color='primary' onClick={() => addIntervention()} style={{ height: '35px' }} alignSelf='end' margin={{ bottom: 'small' }} />}
                </Box>
              </>}
          </Box>
          {(localIncidentValues.restrictedPhysicalInterventionUsed === 'Yes' || localIncidentValues.medicalInterventionRequired === 'Yes') &&
            <FormField
              label={questions[6].questionText}
              name={questions[6].key}
            >
              {!editingDisabled && <TextArea
                name={questions[6].key}
              />}
              {editingDisabled && <Text>{props.incidentValues?.[questions[6].key] || 'n/a'}</Text>}

            </FormField>}

          <Divider color='primary' margin={{ top: 'medium', bottom: 'none' }} />

          <Box direction='row' gap='small' justify='between' margin={{ top: 'medium' }}>
            <Button label='< Back' onClick={() => previousPage()} secondary />
            {!editingDisabled && <Button icon={<FontAwesome icon={['fal', 'save']} color='#fff' />} label='Save for later' onClick={() => submitForm()} />}
            <Button label='Next Page' onClick={() => nextPage()} primary />
          </Box>
        </Form>
      </Box>
    </Box>
  )
}

export default MedicalIntervention
